import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { SwmmProject } from "../model/inp/types.ts";
import { PLANE, type CrsChoice } from "../model/geo/crs.ts";
import { preloadEpsg, searchEpsg, toCrsChoice, type EpsgEntry } from "../model/geo/epsgSearch.ts";
import { validateCrs } from "../model/geo/validateCrs.ts";
import { useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";

/**
 * Lets the user say whether a project is georeferenced and, if so, which CRS
 * its `[COORDINATES]` are in — SWMM's `.inp` format never says, and
 * coordinate values alone can't tell an arbitrary local plane from a real
 * projected CRS without knowing where in the world the project is, so this
 * is always an explicit user choice.
 *
 * Backed by the real EPSG registry (~7,000 entries), searchable by name or
 * code via a custom combobox (a native `<datalist>` is unwieldy at that
 * size). A raw proj4 string (e.g. pasted from epsg.io) also works, detected
 * by its `+proj=` prefix, for a CRS the registry lacks a clean string for.
 *
 * Every pick is sanity-checked (validateCrs.ts) against the project's own
 * coordinates before being applied — a real CRS that just doesn't match
 * this project's numbers reports back `onChange(PLANE)` and shows why,
 * instead of quietly producing a broken georeferenced view.
 */
export function ProjectionPicker({
  project,
  value,
  onChange,
}: {
  project: SwmmProject;
  value: CrsChoice;
  onChange: (choice: CrsChoice) => void;
}) {
  const t = useT();
  const [georeferenced, setGeoreferenced] = useState(value.proj4 != null);
  const [text, setText] = useState(value.proj4 != null ? value.label : "");
  const [results, setResults] = useState<EpsgEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidReason, setInvalidReason] = useState<TextKey | null>(null);
  /** Without this, a genuine `searchEpsg` failure left `loading` stuck `true` forever, the dropdown frozen on "Searching…" with no way out. */
  const [searchError, setSearchError] = useState<string | null>(null);
  // Guards against a slow earlier search response overwriting a faster
  // later one — only the most recent request's result is ever applied.
  const requestId = useRef(0);
  // Debounces the searchEpsg() scan (a full pass over ~7,000 entries) so a
  // fast typist doesn't trigger one per keystroke.
  const debounceTimer = useRef<number | null>(null);
  // The blur-close timer (below) is delayed 150ms so a click on a result can
  // register before the list unmounts — tracked here (not a bare inline
  // `setTimeout`) so it can be cleared on unmount too, same as `debounceTimer`.
  const blurTimer = useRef<number | null>(null);
  // Which result the keyboard is currently on — Up/Down move it, Enter picks
  // it. `null` means "no result highlighted yet" (Enter falls back to the
  // first one, same as before this fix).
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  // Neither timer above was ever cleared on unmount — a debounced search (or
  // a pending blur-close) that resolves after the picker itself has already
  // unmounted (e.g. the georeference dialog was just closed) called
  // setState on a dead component.
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    };
  }, []);

  function toggle(checked: boolean) {
    setGeoreferenced(checked);
    setInvalidReason(null);
    if (!checked) {
      setText("");
      setResults([]);
      setOpen(false);
      onChange(PLANE);
    }
  }

  /** Applies a resolved choice if it checks out against the project's coordinates; otherwise reports PLANE and explains why. */
  function apply(choice: CrsChoice) {
    const result = validateCrs(project, choice.proj4!, choice.areaOfUse);
    if (!result.valid) {
      setInvalidReason(result.reason ?? "crsValidation.generic");
      onChange(PLANE);
      return;
    }
    setInvalidReason(null);
    onChange(choice);
  }

  function handleTextChange(next: string) {
    setText(next);
    setOpen(true);
    setInvalidReason(null);
    setHighlightedIndex(null);
    if (debounceTimer.current !== null) window.clearTimeout(debounceTimer.current);
    const trimmed = next.trim();
    if (trimmed.startsWith("+proj=")) {
      apply({ proj4: trimmed, label: t("projectionPicker.customProjection") });
      setResults([]);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    setSearchError(null);
    debounceTimer.current = window.setTimeout(() => {
      searchEpsg(next)
        .then((matches) => {
          if (id !== requestId.current) return; // superseded by a newer keystroke
          setResults(matches);
        })
        .catch((err: unknown) => {
          if (id !== requestId.current) return; // superseded by a newer keystroke
          setResults([]);
          setSearchError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, 150);
  }

  function pick(entry: EpsgEntry) {
    const choice = toCrsChoice(entry);
    setText(choice.label);
    apply(choice);
    setResults([]);
    setOpen(false);
    setHighlightedIndex(null);
  }

  /** Up/Down move a roving highlight over `results`; Enter picks the highlighted one (falling back to the first result when nothing's been highlighted yet); Escape closes the list. */
  function handleInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((current) =>
        current === null ? 0 : Math.min(current + 1, results.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((current) =>
        current === null ? results.length - 1 : Math.max(current - 1, 0)
      );
    } else if (e.key === "Enter") {
      const entry = results[highlightedIndex ?? 0];
      if (entry) {
        e.preventDefault();
        pick(entry);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlightedIndex(null);
    }
  }

  return (
    <div className="projection-picker">
      <label className="projection-picker-toggle">
        <input type="checkbox" checked={georeferenced} onChange={(e) => toggle(e.target.checked)} />
        {t("projectionPicker.georeferencedLabel")}
      </label>

      {georeferenced && (
        <div className="projection-picker-body">
          <input
            type="text"
            value={text}
            onChange={(e) => void handleTextChange(e.target.value)}
            onFocus={() => {
              preloadEpsg();
              setOpen(true);
            }}
            // A click on a result needs to register before the list unmounts.
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => setOpen(false), 150);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={t("projectionPicker.searchPlaceholder")}
          />
          {open && (loading || searchError || results.length > 0) && (
            <ul className="projection-picker-results">
              {loading ? (
                <li className="projection-picker-status">{t("projectionPicker.searching")}</li>
              ) : searchError ? (
                <li className="projection-picker-status projection-picker-error" role="alert">
                  {t("projectionPicker.searchFailed", { message: searchError })}
                </li>
              ) : (
                results.map((entry, index) => (
                  <li key={entry.code}>
                    <button
                      type="button"
                      className={
                        index === highlightedIndex
                          ? "projection-picker-result-highlighted"
                          : undefined
                      }
                      onClick={() => pick(entry)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {entry.name}
                      <span className="projection-picker-code">EPSG:{entry.code}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
          {invalidReason ? (
            <p className="projection-picker-warning">
              {t("projectionPicker.warning", { reason: t(invalidReason) })}
            </p>
          ) : (
            value.proj4 == null && (
              <p className="projection-picker-hint">{t("projectionPicker.hint")}</p>
            )
          )}
        </div>
      )}
    </div>
  );
}
